/**
 * TaskPanel Component
 * 
 * Displays hierarchical task/todo list with progress tracking.
 * Supports nested subtasks with collapsible tree view.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useTaskStore } from '../../stores/taskStore';
import { formatErrorMessage } from '../../utils/errorUtils';
import './TaskPanel.css';

// Status icons using Unicode symbols
const STATUS_ICONS = {
    pending: '○',
    in_progress: '◐',
    completed: '●',
    cancelled: '✕'
};

// Status labels for tooltips
const STATUS_LABELS = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
};

// Agent icons (using simple symbols) and colors for visual identification
const AGENT_CONFIG = {
    // Streamlined Agency (v2)
    'orchestrator': { icon: '◉', color: '#FFD700', label: 'Director' },
    'director': { icon: '◉', color: '#FFD700', label: 'Director' },
    'researcher': { icon: '◎', color: '#00CED1', label: 'Researcher' },
    'designer': { icon: '◆', color: '#E74C3C', label: 'Designer' },
    'developer': { icon: '⬡', color: '#61DAFB', label: 'Developer' },
    'simple_coder': { icon: '▣', color: '#95A5A6', label: 'Coder' },
    // Legacy names (backwards compat)
    'creative_director': { icon: '◉', color: '#FFD700', label: 'Director' },
    'scene_analyst': { icon: '◎', color: '#00CED1', label: 'Researcher' },
    'vision_interpreter': { icon: '◈', color: '#9B59B6', label: 'Designer' },
    'visual_designer': { icon: '◆', color: '#E74C3C', label: 'Designer' },
    'ux_strategist': { icon: '▣', color: '#3498DB', label: 'Designer' },
    'u3d_expert': { icon: '▲', color: '#27AE60', label: 'U3D Expert' },
    'react_developer': { icon: '⬡', color: '#61DAFB', label: 'React Developer' },
    'qa_critic': { icon: '◇', color: '#F39C12', label: 'QA Critic' },
    'default': { icon: '●', color: '#95a5a6', label: 'Agent' }
};

// Helper to get agent config
function getAgentConfig(agentName) {
    if (!agentName) return null;
    const key = agentName.toLowerCase().replace(/\s+/g, '_');
    return AGENT_CONFIG[key] || AGENT_CONFIG['default'];
}

/**
 * Recursive Task Item Component
 * Renders a task and its subtasks in a tree structure (read-only, controlled by agents)
 */
function TaskItem({ task, allTasks, depth = 0, expandedTasks, toggleExpanded }) {

    // Get direct children of this task
    const subtasks = allTasks.filter(t => t.parent_id === task.id);
    const hasSubtasks = subtasks.length > 0;
    const isExpanded = expandedTasks.has(task.id);

    // Calculate subtask completion stats
    const completedSubtasks = subtasks.filter(t => t.status === 'completed').length;
    const subtaskProgress = hasSubtasks ? Math.round((completedSubtasks / subtasks.length) * 100) : 0;

    const handleToggleExpand = (e) => {
        e.stopPropagation();
        toggleExpanded(task.id);
    };

    // Calculate left indent - deeper nesting = more indent
    const indentPx = depth * 24;

    return (
        <div className={`task-tree-item depth-${Math.min(depth, 3)}`}>
            <div
                className={`task-item status-${task.status} ${hasSubtasks ? 'has-subtasks' : ''}`}
                style={{ paddingLeft: `${indentPx + 8}px` }}
            >
                {/* Expand/Collapse Toggle */}
                {hasSubtasks && (
                    <button
                        className="task-expand-toggle"
                        onClick={handleToggleExpand}
                        title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                    >
                        {isExpanded ? '▼' : '▶'}
                    </button>
                )}

                {/* Indent spacer for items without subtasks */}
                {!hasSubtasks && depth > 0 && (
                    <span className="task-tree-spacer" />
                )}

                {/* Status Indicator (read-only) */}
                <span
                    className="task-status-indicator"
                    title={STATUS_LABELS[task.status]}
                >
                    <span className="task-status-icon">{STATUS_ICONS[task.status]}</span>
                </span>

                {/* Content */}
                <div className="task-content-wrapper">
                    <span className="task-content">{task.content}</span>

                    {/* Subtask count badge */}
                    {hasSubtasks && (
                        <span
                            className={`task-subtask-badge ${subtaskProgress === 100 ? 'complete' : ''}`}
                            title={`${completedSubtasks}/${subtasks.length} subtasks completed`}
                        >
                            {completedSubtasks}/{subtasks.length}
                        </span>
                    )}

                    {/* Subtask count already shown above; agent badges removed for cleaner UI */}
                </div>
            </div>

            {/* Subtasks - Recursive rendering */}
            {hasSubtasks && isExpanded && (
                <div className="task-subtasks-container">
                    {/* Connection line */}
                    <div
                        className="task-tree-line"
                        style={{ left: `${indentPx + 16}px` }}
                    />

                    {subtasks.map(subtask => (
                        <TaskItem
                            key={subtask.id}
                            task={subtask}
                            allTasks={allTasks}
                            depth={depth + 1}
                            expandedTasks={expandedTasks}
                            toggleExpanded={toggleExpanded}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Main TaskPanel Component
 * Displays agent-controlled task progress (read-only for users)
 */
function TaskPanel({ projectId, sessionId }) {
    const {
        tasks,
        stats,
        isLoading,
        error,
        isExpanded,
        loadTasks,
        toggleExpanded: togglePanelExpanded,
        activeAgent
    } = useTaskStore();

    // Track which tasks are expanded (show subtasks)
    const [expandedTasks, setExpandedTasks] = useState(new Set());

    // Load tasks when project/session changes
    useEffect(() => {
        if (projectId && sessionId) {
            loadTasks(projectId, sessionId);
        }
    }, [projectId, sessionId, loadTasks]);

    // Auto-expand tasks with subtasks on initial load
    useEffect(() => {
        if (tasks.length > 0) {
            const tasksWithChildren = new Set();
            tasks.forEach(t => {
                if (t.parent_id) {
                    tasksWithChildren.add(t.parent_id);
                }
            });
            // Expand all parent tasks by default
            setExpandedTasks(prev => {
                const newSet = new Set(prev);
                tasksWithChildren.forEach(id => newSet.add(id));
                return newSet;
            });
        }
    }, [tasks]);

    // Toggle expansion of a single task
    const toggleTaskExpanded = useCallback((taskId) => {
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                newSet.delete(taskId);
            } else {
                newSet.add(taskId);
            }
            return newSet;
        });
    }, []);

    // Get root tasks (no parent) 
    const rootTasks = tasks.filter(t => !t.parent_id);

    // Always show panel (at least as minimal header) so users know it exists
    // Only hide during initial loading before session is established
    const hasNoTasks = tasks.length === 0;

    // Calculate hierarchy stats
    const subtaskCount = tasks.length - rootTasks.length;

    return (
        <div className={`task-panel ${isExpanded ? 'expanded' : 'collapsed'} ${hasNoTasks ? 'empty' : ''}`}>
            {/* Header */}
            <div className="task-panel-header" onClick={togglePanelExpanded}>
                <div className="task-panel-title">
                    <svg className="task-panel-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11l3 3L22 4" />
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                    </svg>
                    <h3>Tasks</h3>
                    {hasNoTasks ? (
                        <span className="task-count-badge waiting">
                            {isLoading ? 'Loading...' : 'Waiting for agent...'}
                        </span>
                    ) : (
                        <>
                            <span className="task-count-badge">
                                {stats.completed}/{stats.total}
                            </span>
                            {subtaskCount > 0 && (
                                <span className="task-hierarchy-badge" title="Subtasks">
                                    +{subtaskCount} nested
                                </span>
                            )}
                        </>
                    )}
                </div>

                {/* Active Agent Indicator */}
                {activeAgent && (
                    <div 
                        className="active-agent-indicator"
                        style={{ 
                            '--agent-color': getAgentConfig(activeAgent)?.color || '#95a5a6'
                        }}
                        title={`Currently running: ${activeAgent}`}
                    >
                        <span className="active-agent-icon">
                            {getAgentConfig(activeAgent)?.icon || '●'}
                        </span>
                        <span className="active-agent-name">
                            {activeAgent}
                        </span>
                    </div>
                )}

                <div className="task-panel-actions">
                    <button className="task-panel-expand-btn">
                        {isExpanded ? '▼' : '▶'}
                    </button>
                </div>
            </div>

            {/* Progress bar */}
            {stats.total > 0 && (
                <div className="task-progress-container">
                    <div className="task-progress-bar">
                        <div
                            className="task-progress-fill"
                            style={{ width: `${stats.progress_percent}%` }}
                        />
                    </div>
                    <span className="task-progress-text">{stats.progress_percent}%</span>
                </div>
            )}

            {/* Content (collapsible) */}
            {isExpanded && (
                <div className="task-panel-content">
                    {/* Error state */}
                    {error && (
                        <div className="task-error">
                            {formatErrorMessage(error)}
                        </div>
                    )}

                    {/* Loading state */}
                    {isLoading && tasks.length === 0 && (
                        <div className="task-loading">
                            Loading tasks...
                        </div>
                    )}


                    {/* Task tree */}
                    <div className="task-list task-tree">
                        {rootTasks.map(task => (
                            <TaskItem
                                key={task.id}
                                task={task}
                                allTasks={tasks}
                                depth={0}
                                expandedTasks={expandedTasks}
                                toggleExpanded={toggleTaskExpanded}
                            />
                        ))}
                    </div>

                    {/* Stats summary */}
                    {stats.total > 0 && (
                        <div className="task-stats-summary">
                            {stats.in_progress > 0 && (
                                <span className="stat-in-progress">
                                    {stats.in_progress} in progress
                                </span>
                            )}
                            {stats.pending > 0 && (
                                <span className="stat-pending">
                                    {stats.pending} pending
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TaskPanel;
